/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Form, Image, message } from "antd";
import { AuthApi } from "api/auth";
import { ForgotPasswordFormType } from "api/auth/type";
import {
  LogoVillarreal,
  ModalIconPassword,
  ModalLogo,
  ModalLogoOkVip,
  ModalUserIcon,
} from "assets/common/modal";
import HelmetCustom from "components/common/HelmetCustom";
import InputCustomAuth from "components/common/InputCustomAuth";
import { PATH } from "constants/path";
import { REGEX_EMOJI, REGEX_USERNAME } from "constants/regex";
import { ResponseCode } from "constants/response";
import { useAppSelector } from "hooks/app-hook";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { selectIsAuthenticated } from "store/auth";
import { ApiResponse } from "types/api";
import * as yup from "yup";

import SignInPageStyle from "../SigninPage/style.index";

const schema = yup
  .object({
    username: yup
      .string()
      .trim()
      .lowercase()
      .required("Đây là trường bắt buộc")
      .test("emoji", "Không nhập emoji", (username: string): boolean => {
        if (!username) return false;
        const regex = new RegExp(REGEX_EMOJI);
        return !regex.test(username);
      })
      .max(16, "Độ dài tối đa 16 ký tự")
      .min(6, "Độ dài tối thiểu 6 ký tự")
      .matches(
        REGEX_USERNAME,
        `Phải lớn hơn 5 ký tự,bắt đầu phải bằng chữ cái,không điền các ký tự đặc biệt.`,
      )
      .required("Vui lòng nhập tài khoản"),
    mobile: yup.string().trim().required("Vui lòng nhập số điện thoại"),
    email: yup
      .string()
      .email("Vui lòng nhập đúng định dạng email")
      .trim()
      .required("Vui lòng nhập email"),
  })
  .required();

const ForgetPassword = () => {
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [messageRecovery, setMessageRecovery] = useState<string>("");

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<ForgotPasswordFormType>({
    resolver: yupResolver(schema),
    mode: "all",
  });
  const onSubmit = async (data: ForgotPasswordFormType) => {
    const datForm = {
      ...data,
      mobile: data.mobile.replace("+", ""),
    };
    setIsLoading(true);

    const res = await AuthApi.forgotPassword(datForm.username, datForm);
    setIsLoading(false);
    const dataResult = res.data as ApiResponse;

    if (dataResult.status !== ResponseCode.SUCCESS) {
      message.error({
        key: "forget-password",
        content: dataResult.msg,
      });
      return;
    }
    message.success({
      key: "forget-password",
      content: "Recovery Success!",
    });
    setMessageRecovery(dataResult.msg);
    reset();
  };

  useEffect(() => {
    if (!isAuthenticated) return;

    navigate("/");
  }, []);

  return (
    <div css={SignInPageStyle.wrapper}>
      <HelmetCustom title="Signup" />
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 15px;

          position: relative;
        `}
      >
        <Link to={PATH.Home} css={SignInPageStyle.btnClose}>
          ✖
        </Link>
        <div
          css={css`
            margin: 0 auto 25px;
            width: 77%;
          `}
        >
          <Image
            src={ModalLogo}
            alt=""
            preview={false}
            style={{
              height: "auto",
              width: "100%",
            }}
          />
        </div>
        <div
          css={css`
            display: flex;
            gap: 4px;
            align-items: center;
            justify-content: space-between;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              justify-content: center;
            `}
          >
            <h2
              css={css`
                font-size: 14px;
                margin: 0 0 8px;
                color: #2d7699;
              `}
            >
              Đối tác chính thức
            </h2>
            <p
              css={css`
                color: #3e81a1;
                font-size: 13px;
                font-weight: 300;
              `}
            >
              Villarreal CF & OKVIP
            </p>
          </div>

          <div
            css={css`
              display: flex;
              align-items: center;
            `}
          >
            <div
              css={css`
                border-right: 1px solid #2d7699;
                padding-right: 10px;
              `}
            >
              <Image
                src={LogoVillarreal}
                alt=""
                height={55}
                width={42}
                preview={false}
              />
            </div>
            <div
              css={css`
                padding-left: 10px;
                max-height: 100px;
              `}
            >
              <Image
                src={ModalLogoOkVip}
                alt=""
                height={53}
                width={158}
                preview={false}
              />
            </div>
          </div>
        </div>

        <Form onSubmitCapture={handleSubmit(onSubmit)}>
          <p
            css={css`
              color: #3e81a1;
              font-size: 14px;
              font-weight: 500;
            `}
          >
            {messageRecovery}
          </p>
          <div
            css={css`
              margin-top: 10px;
              display: flex;
              flex-direction: column;
              justify-content: center;
              width: 100%;
              gap: 20px;
            `}
          >
            <InputCustomAuth
              placeholder={"Vui lòng nhập tên tài khoản"}
              name={"username"}
              control={control}
              margin_bottom="12px"
              id={"username"}
              errors={errors.username?.message}
              validate_status={errors.username ? "error" : "success"}
              prefix={
                <Image
                  src={ModalUserIcon}
                  alt=""
                  height={42}
                  width={42}
                  preview={false}
                />
              }
            />

            <InputCustomAuth
              placeholder={"Vui lòng nhập số điện thoại"}
              name={"mobile"}
              control={control}
              margin_bottom="12px"
              id={"mobile"}
              type="mobile"
              errors={errors.mobile?.message}
              validate_status={errors.mobile ? "error" : "success"}
              prefix={
                <Image
                  src={ModalIconPassword}
                  alt=""
                  height={42}
                  width={42}
                  preview={false}
                />
              }
            />

            <InputCustomAuth
              placeholder={"Vui lòng nhập email"}
              name={"email"}
              control={control}
              margin_bottom="12px"
              id={"email"}
              errors={errors.email?.message}
              validate_status={errors.email ? "error" : "success"}
              prefix={
                <Image
                  src={ModalIconPassword}
                  alt=""
                  height={42}
                  width={42}
                  preview={false}
                />
              }
            />

            <div
              css={css`
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                gap: 10px;
              `}
            >
              <div css={css``}>
                <p
                  css={css`
                    font-size: 16px;
                    text-align: center;
                    line-height: 1.2;
                    font-weight: 400;
                  `}
                >
                  Lấy lại mật khẩu
                </p>
                <Button
                  disabled={isLoading}
                  htmlType="submit"
                  css={css`
                    margin-top: 8px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    background-color: #2d7699;
                    border-radius: 50px;
                    font-weight: 700;
                    font-size: 20px;
                    width: 100%;
                    height: 58px;
                    text-transform: uppercase;
                  `}
                >
                  <span>Xác nhận</span>
                </Button>
              </div>

              <div css={css``}>
                <p
                  css={css`
                    font-size: 16px;
                    text-align: center;
                    line-height: 1.2;
                    font-weight: 400;
                  `}
                >
                  Bạn đã có tài khoản ?
                </p>
                <Button
                  onClick={() => navigate(PATH.Account.login)}
                  css={css`
                    margin-top: 8px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    background-color: #404040;
                    border-radius: 50px;
                    font-weight: 700;
                    font-size: 20px;
                    width: 100%;
                    height: 58px;
                    text-transform: uppercase;
                  `}
                >
                  <span>Đăng nhập</span>
                </Button>
              </div>
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default ForgetPassword;
